import axios from 'axios';

// REACT_APP_BACKEND_URL must be set in .env — fallback prevents undefined/api in console
const _BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
if (!_BACKEND) {
  console.error('[api] REACT_APP_BACKEND_URL is not set. API calls will fail. Check your .env file.');
}
const API_URL = _BACKEND ? `${_BACKEND}/api` : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tg247_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Parse a rate-limit detail string from the backend.
 * Format: "RATE_LIMITED:{retryAfterSeconds}:{human message}"
 * Returns null if the string is not a rate-limit detail.
 */
export function parseRateLimit(detail) {
  if (typeof detail !== 'string' || !detail.startsWith('RATE_LIMITED:')) return null;
  const parts = detail.split(':');
  // parts[0] = "RATE_LIMITED", parts[1] = seconds, parts[2..] = message (may contain colons)
  const retryAfter = parseInt(parts[1], 10);
  const message = parts.slice(2).join(':');
  return { retryAfter: isNaN(retryAfter) ? 0 : retryAfter, message };
}

// Auto-logout on 401 + friendly rate-limit toast on 429
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('tg247_token');
        localStorage.removeItem('tg247_user');
        const nextPath = currentPath + window.location.search;
        window.location.href = `/login?session=expired&next=${encodeURIComponent(nextPath)}`;
      }
    }

    if (status === 429) {
      const rawDetail = error.response?.data?.detail;
      const rl = parseRateLimit(rawDetail);
      if (rl) {
        // Rewrite detail to the human-readable message so components can display it directly
        if (error.response?.data) error.response.data.detail = rl.message;
        error.isRateLimit = true;
        error.retryAfter = rl.retryAfter;
      } else {
        // Generic 429 — still make it friendly
        const friendly = '⏳ Bạn thao tác quá nhanh! Vui lòng thử lại sau ít phút.';
        if (error.response?.data) error.response.data.detail = friendly;
        error.isRateLimit = true;
        error.retryAfter = 60;
      }
      // Dispatch a global DOM event so App.js can show a toast without needing React context here
      window.dispatchEvent(new CustomEvent('api:rateLimit', {
        detail: { message: error.response?.data?.detail, retryAfter: error.retryAfter }
      }));
    }

    return Promise.reject(error);
  }
);

// Public API (no auth required)
export const publicAPI = {
  getStats: async () => {
    const response = await api.get('/stats');
    return response.data;
  },
  getCategories: async () => {
    const response = await api.get('/admin/categories/public');
    return response.data;
  },
  trackVisit: async () => {
    try { await api.post('/visit'); } catch {}
  },
  submitContact: async (form) => {
    const response = await api.post('/contact', form);
    return response.data;
  },
};

// Auth API
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    if (response.data.access_token) {
      localStorage.setItem('tg247_token', response.data.access_token);
      localStorage.setItem('tg247_user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  verifyRegistrationOtp: async (email, otp, firebase_token) => {
    // firebase_token in body (not query) to avoid appearing in logs/history
    const response = await api.post('/auth/verify-registration-otp', {
      email,
      ...(otp           ? { otp }           : {}),
      ...(firebase_token ? { firebase_token } : {}),
    });
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', null, { params: { email } });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  },
};

// Transaction API
export const transactionAPI = {
  create: async (data) => {
    const response = await api.post('/transactions', data);
    return response.data;
  },
  
  getAll: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/transactions', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await api.put(`/transactions/${id}/status`, null, { 
      params: { new_status: status } 
    });
    return response.data;
  },
  
  accept: async (id) => {
    const response = await api.post(`/transactions/${id}/accept`);
    return response.data;
  },

  decline: async (id) => {
    const response = await api.post(`/transactions/${id}/decline`);
    return response.data;
  },

  requestExtension: async (id, phase) => {
    const response = await api.post(`/transactions/${id}/request-extension`, phase ? { phase } : {});
    return response.data;
  },

  respondExtension: async (id, accept) => {
    const response = await api.post(`/transactions/${id}/respond-extension`, null, {
      params: { accept },
    });
    return response.data;
  },

  approve: async (id) => {
    const response = await api.post(`/transactions/${id}/approve`);
    return response.data;
  },
  
  sellerConfirm: async (id) => {
    const response = await api.post(`/transactions/${id}/seller-confirm`);
    return response.data;
  },

  buyerConfirm: async (id) => {
    const response = await api.post(`/transactions/${id}/buyer-confirm`);
    return response.data;
  },

  submitPayment: async (id) => {
    const response = await api.post(`/transactions/${id}/submit-payment`);
    return response.data;
  },

  payFromWallet: async (id) => {
    const response = await api.post(`/transactions/${id}/pay-from-wallet`);
    return response.data;
  },

  getDisputeMessages: async (id) => {
    const response = await api.get(`/transactions/${id}/dispute-messages`);
    return response.data;
  },

  sendDisputeMessage: async (id, message, media = null) => {
    const response = await api.post(`/transactions/${id}/dispute-messages`, { message, media });
    return response.data;
  },

  getTxnChatMessages: async (id) => {
    const response = await api.get(`/transactions/${id}/chat-messages`);
    return response.data;
  },

  sendTxnChatMessage: async (id, message, media = null) => {
    const response = await api.post(`/transactions/${id}/chat-messages`, { message, media });
    return response.data;
  },

  dispute: async (id, reason, evidence = null) => {
    const body = { reason };
    if (evidence) body.evidence = evidence;
    const response = await api.post(`/transactions/${id}/dispute`, body);
    return response.data;
  },

  milestoneSellerDone: async (txnId, idx) => {
    const r = await api.post(`/transactions/${txnId}/milestones/${idx}/seller-complete`);
    return r.data;
  },
  milestoneBuyerConfirm: async (txnId, idx) => {
    const r = await api.post(`/transactions/${txnId}/milestones/${idx}/buyer-confirm`);
    return r.data;
  },
  milestoneAdminDisburse: async (txnId, idx) => {
    const r = await api.post(`/transactions/${txnId}/milestones/${idx}/admin-disburse`);
    return r.data;
  },

  getAuditLog: async (id) => {
    const response = await api.get(`/transactions/${id}/audit-log`);
    return response.data;
  },
};

// Admin Transaction actions
export const adminTransactionAPI = {
  confirmPayment: async (id) => {
    const response = await api.post(`/admin/transactions/${id}/confirm-payment`);
    return response.data;
  },
  disburse: async (id) => {
    const response = await api.post(`/admin/transactions/${id}/disburse`);
    return response.data;
  },
  cancel: async (id) => {
    const response = await api.post(`/admin/transactions/${id}/cancel`);
    return response.data;
  },
  getDisputeMessages: async (id) => {
    const response = await api.get(`/admin/disputes/${id}/messages`);
    return response.data;
  },
  sendDisputeMessage: async (id, message, media = null) => {
    const response = await api.post(`/admin/disputes/${id}/messages`, { message, media });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  
  getAllTransactions: async () => {
    const response = await api.get('/admin/transactions');
    return response.data;
  },
  
  getAllUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  
  getDisputes: async () => {
    const response = await api.get('/admin/disputes');
    return response.data;
  },
  
  resolveDispute: async (id, resolution, winner) => {
    const response = await api.put(`/admin/transactions/${id}/resolve`, { resolution, winner });
    return response.data;
  },

  saveTransactionNote: async (transactionId, note) => {
    const response = await api.post(`/admin/transactions/${transactionId}/note`, { note });
    return response.data;
  },
  
  updateUserStatus: async (id, verified) => {
    const response = await api.put(`/admin/users/${id}/status`, null, {
      params: { verified }
    });
    return response.data;
  },

  updateUserRole: async (id, role) => {
    const response = await api.put(`/admin/users/${id}/role`, null, { params: { role } });
    return response.data;
  },

  editUser: async (id, data) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  banUser: async (id) => {
    const response = await api.post(`/admin/users/${id}/ban`);
    return response.data;
  },

  unbanUser: async (id) => {
    const response = await api.post(`/admin/users/${id}/unban`);
    return response.data;
  },

  getUserDetail: async (id) => {
    const response = await api.get(`/admin/users/${id}/detail`);
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSettings: async (data) => {
    const response = await api.put('/admin/settings', data);
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get('/admin/categories');
    return response.data;
  },

  updateCategories: async (categories) => {
    const response = await api.put('/admin/categories', { categories });
    return response.data;
  },

  getWalletHistory: async () => {
    const response = await api.get('/admin/wallet/history');
    return response.data;
  },

  getStatsWithDate: async (dateFrom, dateTo) => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    const r = await api.get('/admin/stats', { params });
    return r.data;
  },

  getSystemWallet: async (params = {}) => {
    const cleaned = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    const q = new URLSearchParams(cleaned).toString();
    const r = await api.get(`/admin/system-wallet${q ? '?' + q : ''}`);
    return r.data;
  },

  getLedger: async (params = {}) => {
    const cleaned = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    const q = new URLSearchParams(cleaned).toString();
    const r = await api.get(`/admin/ledger${q ? '?' + q : ''}`);
    return r.data;
  },

  getBlockchainTransactions: async (params = {}) => {
    const cleaned = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    const q = new URLSearchParams(cleaned).toString();
    const r = await api.get(`/admin/wallet/blockchain-transactions${q ? '?' + q : ''}`);
    return r.data;
  },

  syncTatumWebhooks: async () => {
    const r = await api.post('/admin/wallet/blockchain-transactions/sync-webhooks');
    return r.data;
  },

  getTatumSettings: async () => {
    const r = await api.get('/admin/settings/tatum');
    return r.data;
  },

  saveTatumSettings: async (data) => {
    const r = await api.post('/admin/settings/tatum', data);
    return r.data;
  },
};

// Guest chat API (no auth required)
export const guestChatAPI = {
  startSession: async (guestName, guestEmail, guestId) => {
    const response = await api.post('/chat/guest/start', { guest_name: guestName, guest_email: guestEmail, guest_id: guestId });
    return response.data;
  },
  getMessages: async (sessionId, guestId) => {
    const response = await api.get(`/chat/guest/messages/${sessionId}`, { params: { guest_id: guestId } });
    return response.data;
  },
  sendMessage: async (sessionId, guestId, message, media = '') => {
    const response = await api.post(`/chat/guest/message/${sessionId}`, { guest_id: guestId, message, media });
    return response.data;
  },
  // Merge guest chat history into authenticated account after login
  mergeSession: async (guestId) => {
    const response = await api.post('/chat/guest/merge', { guest_id: guestId });
    return response.data;
  },
};

// Wallet API
export const walletAPI = {
  getBalance: async () => {
    const response = await api.get('/wallet/balance');
    return response.data;
  },
  
  createDeposit: async (walletType, amount, network = null, purpose = 'wallet', transactionId = null) => {
    const response = await api.post('/wallet/deposit', null, {
      params: { wallet_type: walletType, amount, network, purpose, transaction_id: transactionId }
    });
    return response.data;
  },
  
  getDeposits: async () => {
    const response = await api.get('/wallet/deposits');
    return response.data;
  },

  getWalletLogs: async () => {
    const response = await api.get('/wallet/logs');
    return response.data;
  },
  
  // Admin
  getPendingDeposits: async () => {
    const response = await api.get('/wallet/admin/deposits/pending');
    return response.data;
  },
  
  confirmDeposit: async (depositId) => {
    const response = await api.post(`/wallet/admin/deposits/${depositId}/confirm`);
    return response.data;
  },

  cancelDeposit: async (depositId) => {
    const response = await api.post(`/wallet/admin/deposits/${depositId}/cancel`);
    return response.data;
  },

  confirmDepositRegardless: async (depositId) => {
    const response = await api.post(`/wallet/admin/deposits/${depositId}/confirm-regardless`);
    return response.data;
  },

  // User: get specific deposit status (for polling)
  getDepositStatus: async (depositId) => {
    const response = await api.get(`/wallet/deposits/${depositId}`);
    return response.data;
  },

  // User: cancel own pending deposit
  userCancelDeposit: async (depositId) => {
    const response = await api.post(`/wallet/deposits/${depositId}/cancel`);
    return response.data;
  },
  
  addCryptoNetwork: async (network, walletAddress, qrCodeImage) => {
    const response = await api.post('/wallet/admin/crypto-networks', {
      network,
      wallet_address: walletAddress,
      qr_code_image: qrCodeImage
    });
    return response.data;
  },
  
  getCryptoNetworks: async () => {
    // Admin: full list (for admin panel)
    const response = await api.get('/wallet/admin/crypto-networks');
    return response.data;
  },

  // User-facing: only active networks/banks (no admin role required)
  getActiveNetworks: async () => {
    const response = await api.get('/wallet/active-networks');
    return response.data;
  },

  getActiveBanks: async () => {
    const response = await api.get('/wallet/active-banks');
    return response.data;
  },
  
  addBank: async (bankCode, bankName, accountNumber, accountName) => {
    const response = await api.post('/wallet/admin/banks', {
      bank_code: bankCode,
      bank_name: bankName,
      account_number: accountNumber,
      account_name: accountName
    });
    return response.data;
  },
  
  getBanks: async () => {
    const response = await api.get('/wallet/admin/banks');
    return response.data;
  },

  toggleBank: async (id) => {
    const r = await api.patch(`/wallet/admin/banks/${id}/toggle`);
    return r.data;
  },

  editBank: async (id, bankCode, bankName, accountNumber, accountName) => {
    const r = await api.put(`/wallet/admin/banks/${id}`, {
      bank_code: bankCode, bank_name: bankName,
      account_number: accountNumber, account_name: accountName
    });
    return r.data;
  },

  deleteBank: async (id) => {
    const r = await api.delete(`/wallet/admin/banks/${id}`);
    return r.data;
  },

  toggleCryptoNetwork: async (id) => {
    const r = await api.patch(`/wallet/admin/crypto-networks/${id}/toggle`);
    return r.data;
  },

  editCryptoNetwork: async (id, network, walletAddress, qrCodeImage) => {
    const r = await api.put(`/wallet/admin/crypto-networks/${id}`, {
      network, wallet_address: walletAddress, qr_code_image: qrCodeImage || null
    });
    return r.data;
  },

  deleteCryptoNetwork: async (id) => {
    const r = await api.delete(`/wallet/admin/crypto-networks/${id}`);
    return r.data;
  },

  getBankList: async () => {
    const response = await api.get('/wallet/banks/list');
    return response.data;
  },

  verifyWithdrawalOtp: async (firebase_token) => {
    const r = await api.post('/wallet/verify-withdrawal-otp', { firebase_token });
    return r.data;
  },

  createWithdrawalRequest: async (wallet_type, amount, method_id, phone_session_token) => {
    const r = await api.post('/wallet/withdrawal-request', {
      wallet_type, amount, method_id, phone_session_token,
    });
    return r.data;
  },

  getWithdrawalRequests: async () => {
    const r = await api.get('/wallet/withdrawal-requests');
    return r.data;
  },

  cancelWithdrawalRequest: async (reqId) => {
    const r = await api.post(`/wallet/withdrawal-requests/${reqId}/cancel`);
    return r.data;
  },

  adminGetWithdrawalRequests: async () => {
    const r = await api.get('/wallet/admin/withdrawal-requests');
    return r.data;
  },

  adminApproveWithdrawal: async (id) => {
    const r = await api.post(`/wallet/admin/withdrawal-requests/${id}/approve`);
    return r.data;
  },

  adminRejectWithdrawal: async (id) => {
    const r = await api.post(`/wallet/admin/withdrawal-requests/${id}/reject`);
    return r.data;
  },

  getWithdrawalMethods: async () => {
    const response = await api.get('/wallet/withdrawal-methods');
    return response.data;
  },

  addWithdrawalMethod: async (type, label, details) => {
    const response = await api.post('/wallet/withdrawal-methods', { type, label, details });
    return response.data;
  },

  deleteWithdrawalMethod: async (methodId) => {
    const response = await api.delete(`/wallet/withdrawal-methods/${methodId}`);
    return response.data;
  },

  getExchangeRate: async () => {
    const response = await api.get('/wallet/exchange-rate');
    return response.data;
  },

  convertCurrency: async (fromType, toType, amount) => {
    const response = await api.post('/wallet/convert', { from_type: fromType, to_type: toType, amount });
    return response.data;
  },

  expireOldDeposits: async () => {
    const response = await api.post('/wallet/deposits/expire-old');
    return response.data;
  },
};

export const notificationAPI = {
  getAll: async () => { const r = await api.get('/notifications'); return r.data; },
  getUnreadCount: async () => { const r = await api.get('/notifications/unread-count'); return r.data; },
  markRead: async (id) => { const r = await api.put(`/notifications/${id}/read`); return r.data; },
  markAllRead: async () => { const r = await api.put('/notifications/read-all'); return r.data; },
  deleteOne: async (id) => { const r = await api.delete(`/notifications/${id}`); return r.data; },
};

// Chat API
export const chatAPI = {
  getOrCreateSession: async () => {
    const response = await api.post('/chat/session');
    return response.data;
  },
  
  sendMessage: async (message) => {
    const response = await api.post('/chat/messages', { message });
    return response.data;
  },
  
  getMessages: async (sessionId = null) => {
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await api.get('/chat/messages', { params });
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/chat/unread-count');
    return response.data; // { count: number }
  },
  
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/chat/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Admin
  getAllSessions: async () => {
    const response = await api.get('/chat/admin/sessions');
    return response.data;
  },
  
  adminSendMessage: async (sessionId, message) => {
    const response = await api.post(`/chat/admin/messages/${sessionId}`, { message });
    return response.data;
  },

  markSessionRead: async (sessionId) => {
    const response = await api.patch(`/chat/admin/sessions/${sessionId}/read`);
    return response.data;
  },

  recallMessage: async (messageId) => {
    const response = await api.delete(`/chat/messages/${messageId}`);
    return response.data;
  },

  sendImage: async (imageData) => {
    const response = await api.post('/chat/messages/image', { image_data: imageData });
    return response.data;
  },

  adminSendImage: async (sessionId, imageData) => {
    const response = await api.post(`/chat/admin/messages/${sessionId}`, { message: `[media]${imageData}` });
    return response.data;
  },

  adminUploadMedia: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/chat/admin/upload-media/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  sendTyping: async (sessionId, isTyping = true) => {
    try {
      await api.post('/chat/typing', { session_id: sessionId, is_typing: isTyping });
    } catch {}
  },

  tagSession: async (sessionId, tag) => {
    const response = await api.patch(`/chat/admin/sessions/${sessionId}/tag`, { tag });
    return response.data;
  },

  pinSession: async (sessionId, pinned) => {
    const response = await api.patch(`/chat/admin/sessions/${sessionId}/pin`, { pinned });
    return response.data;
  },

  saveNote: async (sessionId, note) => {
    const response = await api.patch(`/chat/admin/sessions/${sessionId}/note`, { note });
    return response.data;
  },
};

// Blog API
export const blogAPI = {
  getPosts: async (page = 1, limit = 12, category = null, tag = null, q = null) => {
    const params = { page, limit };
    if (category) params.category = category;
    if (tag) params.tag = tag;
    if (q) params.q = q;
    const response = await api.get('/blog/posts', { params });
    return response.data;
  },
  getCategories: async () => {
    const response = await api.get('/blog/categories');
    return response.data;
  },
  getAllPosts: async () => {
    const response = await api.get('/blog/posts/all');
    return response.data;
  },
  getPost: async (slug) => {
    const response = await api.get(`/blog/posts/${slug}`);
    return response.data;
  },
  getPostDetail: async (id) => {
    // Admin-only: returns full post content without incrementing view counter
    const response = await api.get(`/blog/detail/${id}`);
    return response.data;
  },
  createPost: async (data) => {
    const response = await api.post('/blog/posts', data);
    return response.data;
  },
  updatePost: async (id, data) => {
    const response = await api.put(`/blog/posts/${id}`, data);
    return response.data;
  },
  deletePost: async (id) => {
    const response = await api.delete(`/blog/posts/${id}`);
    return response.data;
  },
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post('/blog/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  lookupBankAccount: async (bin, account_number) => {
    const response = await api.post('/wallet/lookup-bank-account', { bin, account_number });
    return response.data;
  },
  // Static pages CMS
  listStaticPages: async () => {
    const response = await api.get('/blog/admin/static-pages');
    return response.data;
  },
  updateStaticPage: async (slug, data) => {
    const response = await api.put(`/blog/admin/static-pages/${slug}`, data);
    return response.data;
  },
  getStaticPagePublic: async (slug) => {
    const response = await api.get(`/blog/page/${slug}`);
    return response.data;
  },
};

// ── CMS Manager API ────────────────────────────────────────────────────────
export const cmsAPI = {
  // Categories
  getCategories: async () => (await api.get('/cms/categories')).data,
  createCategory: async (data) => (await api.post('/cms/categories', data)).data,
  updateCategory: async (slug, data) => (await api.put(`/cms/categories/${slug}`, data)).data,
  deleteCategory: async (slug) => (await api.delete(`/cms/categories/${slug}`)).data,
  syncCategories: async () => (await api.post('/cms/categories/sync')).data,

  // Tags
  getTags: async () => (await api.get('/cms/tags')).data,
  deleteTag: async (name) => (await api.delete(`/cms/tags/${encodeURIComponent(name)}`)).data,
  renameTag: async (old_name, new_name) => (await api.put('/cms/tags/rename', { old_name, new_name })).data,

  // Redirects
  getRedirects: async () => (await api.get('/cms/redirects')).data,
  createRedirect: async (data) => (await api.post('/cms/redirects', data)).data,
  deleteRedirect: async (id) => (await api.delete(`/cms/redirects/${id}`)).data,

  // Robots.txt
  getRobots: async () => (await api.get('/cms/robots')).data,
  updateRobots: async (content) => (await api.put('/cms/robots', { content })).data,
};

export default api;
